import { fetchProductsActionCreator, LOAD_MORE_PRODUCTS, REQUEST_FETCH_PRODUCTS_WITH_SKUS } from '../actions';
import {
  getContentApiUrl,
  getProductsFilters,
  getProductMultipleRequests,
  getProductsPage,
  getProductsPageSize,
  getProductsSliceIds,
  hasMoreProductsPages,
  isProductsBusy,
  isProductsSliceSponsored,
} from '../selectors';
import fetchJson from './fetchJson';
import createFetchOnHydrationMiddleware from './createFetchOnHydrationMiddleware';
import composeMiddlewares from '../helpers/composeReduxMiddlewares';

const fetchProducts = async (apiUrl, filters, pageSize, page) => (
  fetchJson('products', [apiUrl, 'catalog', 'products'].join('/'), {
    ...filters,
    page_size: pageSize,
    page,
  })
);

const fetchMultipleProducts = async (apiUrl, requests, page) => {
  const responses = await Promise.all(
    requests.map(({ filters, pageSize }) => (
      fetchProducts(apiUrl, filters, pageSize, page)
    )),
  );
  return responses.reduce(
    (collection, { meta, products }) => ({
      meta,
      products: [...(collection.products || []), ...products],
    }),
    {},
  );
};

const dispatchFetchProducts = (store, sliceId) => {
  const state = store.getState();
  const apiUrl = getContentApiUrl(state);

  if (isProductsSliceSponsored(state, sliceId)) {
    // DEV NOTE:  List of skus only available after sponsored brands is fetched
    //  Will be handled in middlewares/sponsoredBrands.js
    return;
  }

  // Regular products handling
  const filters = getProductsFilters(state, sliceId);
  const multipleRequests = getProductMultipleRequests(state, sliceId);
  const pageSize = getProductsPageSize(state, sliceId);
  const page = getProductsPage(state, sliceId) + 1;

  store.dispatch(
    fetchProductsActionCreator(
      sliceId,
      multipleRequests && multipleRequests.length
        ? fetchMultipleProducts(apiUrl, multipleRequests, page)
        : fetchProducts(apiUrl, filters, pageSize, page),
    ),
  );
};

const dispatchFetchProductsWithSkus = (store, action) => {
  const state = store.getState();
  const apiUrl = getContentApiUrl(state);
  const { sliceId, skus } = action.payload;
  const { overwriteItemSetItems } = action.meta || {};
  store.dispatch(
    fetchProductsActionCreator(
      sliceId,
      fetchProducts(apiUrl, { skus }, skus.length, 1),
      { overwriteItemSetItems },
    ),
  );
};

const handleLoadMoreProducts = (store, action) => {
  const { sliceId } = action.meta;
  const state = store.getState();
  if (!isProductsBusy(state, sliceId) && hasMoreProductsPages(state, sliceId)) {
    dispatchFetchProducts(store, sliceId);
  }
};

export default composeMiddlewares(
  createFetchOnHydrationMiddleware(getProductsSliceIds, dispatchFetchProducts),
  store => next => action => {
    if (action.type === LOAD_MORE_PRODUCTS) {
      // the LOAD_MORE_PRODUCTS action doesn’t need to go any further so don’t call next()
      // afterwards
      return handleLoadMoreProducts(store, action);
    }
    if (action.type === REQUEST_FETCH_PRODUCTS_WITH_SKUS) {
      return dispatchFetchProductsWithSkus(store, action);
    }
    return next(action);
  },
);
